<template>
  <div style="width: 900px; max-width: 96vw;">
    <q-form @submit="onSubmit" class="form-wrap">

      <!-- Инициатор заявки -->
      <div class="form-1c__row">
        <label class="form-1c__label">Инициатор заявки</label>
        <div class="form-1c__field">
          <CustomSelect
            v-if="initiatorTypesLoaded && initiatorTypes.length > 0"
            :options="initiatorTypes"
            optionValueKey="provider_request_initiator_type_id"
            optionLabelKey="provider_request_initiator_type_name"
            placeholder="Выберите инициатора"
            :value="formData.provider_request_initiator_type_id || ''"
            :disabled="loading"
            @change="(value, row) => onChangeField('provider_request_initiator_type_id', value, row)"
          />
          <div v-else-if="initiatorTypesLoaded && initiatorTypes.length === 0" class="form-1c__field--warning">
            Нет типов инициаторов
          </div>
          <div v-else class="form-1c__field--placeholder">Загрузка...</div>
        </div>
      </div>

      <!-- Склад -->
      <div class="form-1c__row">
        <label class="form-1c__label">Склад</label>
        <div class="form-1c__field">
          <div v-if="!selectedInitiatorType || isContractorInitiator" class="form-1c__field--placeholder">—</div>
          <template v-else>
            <CustomSelect
              v-if="skladOptions.length > 1"
              :options="skladOptions"
              optionValueKey="sbs_sklad_id"
              optionLabelKey="naimenovanie"
              placeholder="Выберите склад"
              :value="formData.sbs_sklad_id || ''"
              :disabled="loading"
              @change="(value, row) => onChangeField('sbs_sklad_id', value, row)"
            />
            <div v-else-if="skladOptions.length === 1" class="form-1c__field--readonly">
              {{ skladOptions[0].naimenovanie }}
            </div>
            <div v-else-if="skladLoaded && skladOptions.length === 0" class="form-1c__field--warning">
              Нет склада
            </div>
            <div v-else class="form-1c__field--placeholder">—</div>
            <div class="form-1c__hint">На этот склад будет оприходован материал</div>
          </template>
        </div>
      </div>

      <!-- Дата отгрузки -->
      <div class="form-1c__row">
        <label class="form-1c__label">Дата отгрузки <span class="required">*</span></label>
        <div class="form-1c__field">
          <CustomDate
            placeholder="Выберите дату"
            :required="true"
            @change_date="(value) => onChangeField('date_shipping', value)"
            :value="formData.date_shipping"
            :fullWidth="true"
            :disablePast="true"
            :disabled="loading"
          />
        </div>
      </div>

      <!-- Таблица материалов -->
      <div v-if="formData.provider_request_initiator_type_id" class="materials-section">
        <div class="materials-header">
          <span class="materials-title">Материалы</span>
          <CustomLoader v-if="dataLoading" small />
        </div>

        <div v-if="!dataLoading && allMaterials.length === 0 && dataLoaded" class="no-materials">
          Материалов не найдено
        </div>

        <div v-if="allMaterials.length > 0" class="materials-search-wrap">
          <q-input
            v-model="materialSearch"
            dense
            outlined
            clearable
            placeholder="Поиск по материалу..."
            class="materials-search"
          >
            <template #prepend>
              <q-icon name="search" />
            </template>
          </q-input>
          <span v-if="materialSearch" class="materials-search-count">
            Найдено: {{ filteredMaterials.length }} из {{ allMaterials.length }}
          </span>
        </div>

        <div v-if="allMaterials.length > 0" class="materials-table-wrap">
          <table class="materials-table">
            <thead>
              <tr>
                <th class="col-name">Материал</th>
                <th class="col-price-unit">Цена / ед.</th>
                <th class="col-limits">Заказано / лимит / можно</th>
                <th class="col-order">Кол-во / сумма</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="mat in filteredMaterials" :key="mat.material_id">
                <tr
                  :class="{
                    'row-active': getQty(mat.material_id) > 0,
                    'row-no-agreement': mat.agreements.length === 0,
                  }"
                >
                  <td class="col-name">
                    <div class="mat-name">{{ mat.material_name }}</div>
                    <div class="mat-agreement">
                      <span v-if="mat.agreements.length === 0" class="no-agreement-text">
                        <span class="agreement-label agreement-label--none">Договор:</span>
                        Нет договора поставки
                      </span>
                      <span v-else-if="mat.agreements.length === 1" class="single-agreement-text">
                        <span class="agreement-label">Договор:</span>
                        {{ mat.agreements[0].agreement_display_name }}
                      </span>
                      <div v-else class="agreement-select-wrap">
                        <span class="agreement-label">Договор:</span>
                        <CustomSelect
                          :options="mat.agreements"
                          optionValueKey="contractor_agreement_list_id"
                          optionLabelKey="agreement_display_name"
                          placeholder="Выберите договор"
                          :value="materialAgreementSelection[mat.material_id] || ''"
                          :disabled="loading"
                          @change="(value) => onSelectAgreement(mat.material_id, value)"
                        />
                      </div>
                    </div>
                  </td>

                  <td class="col-price-unit">
                    <div class="cell-stack">
                      <div class="cell-main">
                        <q-spinner v-if="getMatDisplayData(mat).priceLoading" size="14px" color="primary" />
                        <template v-else>
                          <span v-if="getMatDisplayData(mat).price">
                            {{ numberWithCommas(roundMoney(getMatDisplayData(mat).price)) }} ₸
                          </span>
                          <span v-else class="no-price">—</span>
                        </template>
                      </div>
                      <div class="cell-sub">{{ getMatDisplayData(mat).unit_name || '—' }}</div>
                    </div>
                  </td>

                  <td class="col-limits">
                    <div v-if="getMatDisplayData(mat).hasAgreement" class="cell-stack">
                      <div class="cell-row">
                        <span class="cell-label">Заказано:</span>
                        <span :class="{ 'qty-zero': !getMatDisplayData(mat).ordered_cnt }">
                          {{ fmtQty(getMatDisplayData(mat).ordered_cnt) }} {{ getMatDisplayData(mat).unit_name || '' }}
                        </span>
                      </div>
                      <div class="cell-row">
                        <span class="cell-label">Лимит:</span>
                        <span :class="{ 'qty-zero': !getMatDisplayData(mat).smeta_cnt }">
                          {{ fmtQty(getMatDisplayData(mat).smeta_cnt) }} {{ getMatDisplayData(mat).unit_name || '' }}
                        </span>
                      </div>
                      <div class="cell-row">
                        <span class="cell-label">Можно:</span>
                        <span :class="{ 'qty-zero': getMatDisplayData(mat).canOrder <= 0 }">
                          {{ fmtQty(getMatDisplayData(mat).canOrder) }} {{ getMatDisplayData(mat).unit_name || '' }}
                        </span>
                      </div>
                    </div>
                    <span v-else class="no-price">—</span>
                  </td>

                  <td class="col-order">
                    <div class="cell-stack">
                      <div class="cell-row cell-row--input">
                        <q-input
                          :model-value="getQtyText(mat.material_id)"
                          @update:model-value="(val) => setQtyText(mat.material_id, val)"
                          @blur="() => formatQtyText(mat.material_id)"
                          inputmode="decimal"
                          dense
                          outlined
                          placeholder="0"
                          class="qty-input"
                          :disable="loading || isRowSuccess(mat.material_id) || mat.agreements.length === 0"
                          :input-style="{ textAlign: 'right' }"
                        />
                      </div>
                      <div class="cell-row cell-row--sum">
                        <span v-if="getQty(mat.material_id) > 0 && getMatDisplayData(mat).price" class="row-sum">
                          {{ numberWithCommas(roundMoney(getQty(mat.material_id) * getMatDisplayData(mat).price)) }} ₸
                        </span>
                        <span v-else class="qty-zero">—</span>
                      </div>
                    </div>
                  </td>
                </tr>

                <tr
                  v-if="getRowResult(mat.material_id)"
                  :key="`${mat.material_id}-msg`"
                  class="row-message"
                  :class="getRowResult(mat.material_id).status === 'success' ? 'row-message--success' : 'row-message--error'"
                >
                  <td colspan="4" class="row-message__cell">
                    {{ getRowResult(mat.material_id).message }}
                  </td>
                </tr>
              </template>
              <tr v-if="filteredMaterials.length === 0">
                <td colspan="4" class="no-search-result">
                  Ничего не найдено по запросу «{{ materialSearch }}»
                </td>
              </tr>
            </tbody>
            <tfoot v-if="totalSum > 0">
              <tr>
                <td colspan="3" class="total-label">Итого:</td>
                <td class="total-sum">{{ numberWithCommas(totalSum) }} ₸</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div v-if="selectedCount > 0" class="submit-hint">
        Будет создано {{ selectedCount }} позиций
      </div>

      <CustomModalFooter :btn-loading="loading" :disable-submit="selectedCount === 0 || loading" />
    </q-form>
  </div>
</template>

<script>
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import CustomDate from "@/components/form/CustomDate";
import CustomSelect from "@/components/form/CustomSelect";
import CustomLoader from "@/components/common/CustomLoader";
import CustomModalFooter from "@/components/modal/modals/modal-footer";
import {
  createEntranceMaterialRequestOrThrow,
  getMaterialsByAgreement,
  getMaterialPriceByAgreement,
  getSkladOptions,
  getPurchaseTypes,
  getInitiatorTypes,
} from "@/views/ResidentialSettings/services";
import { numberWithCommas } from "@/services";
import { formatErrorMessage } from "@/services/api/errorHandler";
import { useAppStore } from "@/services/store/app";

export default {
  name: "add-material-request-bulk",
  components: { CustomDate, CustomSelect, CustomLoader, CustomModalFooter },
  props: ["data"],
  data() {
    return {
      initiatorTypes: [],
      initiatorTypesLoaded: false,
      selectedInitiatorType: null,

      materialMap: {},
      dataLoading: false,
      dataLoaded: false,

      skladOptions: [],
      skladLoaded: false,

      purchaseTypeId: null,
      loading: false,

      materialAgreementSelection: {},
      materialPriceOverride: {},
      materialPriceLoading: {},
      qtyTextMap: {},
      rowResultMap: {},

      materialSearch: "",

      formData: {
        provider_request_initiator_type_id: "",
        sbs_sklad_id: "",
        date_shipping: "",
      },
    };
  },
  computed: {
    isContractorInitiator() {
      return this.selectedInitiatorType?.provider_request_initiator_type_code === "CONTRACTOR";
    },
    allMaterials() {
      return Object.values(this.materialMap).sort((a, b) =>
        (a.material_name || "").localeCompare(b.material_name || "")
      );
    },
    filteredMaterials() {
      const q = (this.materialSearch || "").trim().toLowerCase();
      if (!q) return this.allMaterials;
      return this.allMaterials.filter((mat) =>
        (mat.material_name || "").toLowerCase().includes(q)
      );
    },
    selectedCount() {
      return this.allMaterials.filter((mat) => {
        const qty = this.getQty(mat.material_id);
        if (qty <= 0) return false;
        return !!this.getSelectedAgreementId(mat);
      }).length;
    },
    totalSum() {
      let sum = 0;
      for (const mat of this.allMaterials) {
        const qty = this.getQty(mat.material_id);
        if (qty <= 0) continue;
        const displayData = this.getMatDisplayData(mat);
        if (displayData.price) {
          sum += qty * displayData.price;
        }
      }
      return Math.round(sum * 100) / 100;
    },
  },
  methods: {
    numberWithCommas,

    getSelectedAgreementId(mat) {
      if (!mat.agreements || mat.agreements.length === 0) return null;
      if (mat.agreements.length === 1) {
        return mat.agreements[0].contractor_agreement_list_id;
      }
      return this.materialAgreementSelection[mat.material_id] || null;
    },

    getMatDisplayData(mat) {
      const hasAgreement = !!this.getSelectedAgreementId(mat);
      const smeta = Number(mat.smeta_cnt) || 0;
      const ordered = Number(mat.ordered_cnt) || 0;
      const canOrder = Math.max(0, this.roundQty(smeta - ordered));
      const overridePrice = this.materialPriceOverride[mat.material_id];
      const price = overridePrice != null ? overridePrice : mat.material_price;
      return {
        price,
        priceLoading: !!this.materialPriceLoading[mat.material_id],
        unit_name: mat.unit_name,
        smeta_cnt: mat.smeta_cnt,
        ordered_cnt: mat.ordered_cnt,
        canOrder,
        hasAgreement,
      };
    },

    async onSelectAgreement(material_id, contractor_agreement_list_id) {
      this.materialAgreementSelection = {
        ...this.materialAgreementSelection,
        [material_id]: contractor_agreement_list_id,
      };

      if (!contractor_agreement_list_id) return;

      this.materialPriceLoading = { ...this.materialPriceLoading, [material_id]: true };
      try {
        const price = await getMaterialPriceByAgreement(contractor_agreement_list_id, material_id);
        this.materialPriceOverride = {
          ...this.materialPriceOverride,
          [material_id]: price != null ? Number(price) : null,
        };
      } finally {
        const next = { ...this.materialPriceLoading };
        delete next[material_id];
        this.materialPriceLoading = next;
      }
    },

    isRowSuccess(material_id) {
      return this.rowResultMap?.[material_id]?.status === "success";
    },
    clearRowResult(material_id) {
      if (this.rowResultMap?.[material_id] == null) return;
      const next = { ...(this.rowResultMap || {}) };
      delete next[material_id];
      this.rowResultMap = next;
    },
    getRowResult(material_id) {
      return this.rowResultMap?.[material_id] || null;
    },
    setRowResult(material_id, status, message) {
      this.rowResultMap = {
        ...(this.rowResultMap || {}),
        [material_id]: {
          status,
          message: (message ?? "").toString().trim() || (status === "success" ? "Сохранено" : "Ошибка"),
        },
      };
    },

    roundMoney(v) {
      const n = Number(v) || 0;
      return Math.round(n * 100) / 100;
    },
    roundQty(v) {
      const n = Number(v) || 0;
      return Math.round(n * 1000) / 1000;
    },
    fmtQty(v) {
      const n = this.roundQty(v);
      return numberWithCommas(n);
    },

    parseQtyText(val) {
      if (val === undefined || val === null) return 0;
      const s = String(val).replace(/\s+/g, "").replace(",", ".");
      const n = parseFloat(s);
      if (!Number.isFinite(n) || n < 0) return 0;
      return n;
    },
    formatQtyNumber(val) {
      const n = this.roundQty(val);
      if (!n) return "";
      const fixed = n.toFixed(3).replace(/\.?0+$/, "");
      return numberWithCommas(fixed);
    },
    getQtyText(material_id) {
      return this.qtyTextMap[material_id] ?? "";
    },
    setQtyText(material_id, val) {
      const s = String(val ?? "");
      const cleaned = s.replace(/[^\d\s.,]/g, "");
      this.qtyTextMap = { ...this.qtyTextMap, [material_id]: cleaned };
      if (this.rowResultMap?.[material_id]?.status === "error") {
        this.clearRowResult(material_id);
      }
    },
    formatQtyText(material_id) {
      const n = this.parseQtyText(this.qtyTextMap[material_id]);
      this.qtyTextMap = { ...this.qtyTextMap, [material_id]: this.formatQtyNumber(n) };
    },
    getQty(material_id) {
      return this.parseQtyText(this.qtyTextMap[material_id]);
    },

    onChangeField(key, value, row) {
      if (this.loading) return;
      this.formData[key] = value;

      if (key === "provider_request_initiator_type_id") {
        this.selectedInitiatorType = row;
        this.materialMap = {};
        this.dataLoaded = false;
        this.qtyTextMap = {};
        this.rowResultMap = {};
        this.materialAgreementSelection = {};
        this.materialPriceOverride = {};
        this.materialPriceLoading = {};
        if (row?.provider_request_initiator_type_code === "CONTRACTOR") {
          this.formData.sbs_sklad_id = "";
        }
        this.loadData();
      }
    },

    async loadData() {
      if (!this.formData.provider_request_initiator_type_id) return;

      this.dataLoading = true;
      this.dataLoaded = false;
      this.materialMap = {};
      this.qtyTextMap = {};
      this.rowResultMap = {};
      this.materialAgreementSelection = {};

      const params = this.data?.data?.params || {};
      const sbs_project_id = params.sbs_project_id;

      if (!sbs_project_id) {
        this.dataLoading = false;
        this.dataLoaded = true;
        return;
      }

      try {
        const materials = await getMaterialsByAgreement(sbs_project_id, params.project_type_id);

        const newMap = {};
        const selection = {};

        for (const mat of (materials || [])) {
          const agreements = Array.isArray(mat.agreements) ? mat.agreements : [];
          newMap[mat.material_id] = { ...mat, agreements };
          if (agreements.length === 1) {
            selection[mat.material_id] = agreements[0].contractor_agreement_list_id;
          }
        }

        this.materialMap = newMap;
        this.materialAgreementSelection = selection;
      } catch (e) {
        console.error("Ошибка загрузки данных:", e);
      } finally {
        this.dataLoading = false;
        this.dataLoaded = true;
      }
    },

    async loadSkladOptions() {
      this.skladLoaded = false;
      const paramsObj = this.data?.data?.params || {};
      if (paramsObj.sbs_project_id == null) {
        this.skladLoaded = true;
        return;
      }
      const params = { sbs_project_id: paramsObj.sbs_project_id };
      if (paramsObj.sbs_sklad_id != null) params.sbs_sklad_id = paramsObj.sbs_sklad_id;
      const res = await getSkladOptions(params);
      this.skladOptions = res || [];
      this.skladLoaded = true;
      if (this.skladOptions.length === 1) {
        this.formData.sbs_sklad_id = this.skladOptions[0].sbs_sklad_id;
      }
    },

    async loadInitiatorTypes() {
      this.initiatorTypesLoaded = false;
      const res = await getInitiatorTypes();
      this.initiatorTypes = res || [];
      this.initiatorTypesLoaded = true;

      const projectTeam = this.initiatorTypes.find(
        (t) => t.provider_request_initiator_type_code === "PROJECT_TEAM"
      );
      if (projectTeam) {
        this.formData.provider_request_initiator_type_id = projectTeam.provider_request_initiator_type_id;
        this.selectedInitiatorType = projectTeam;
        await this.loadData();
      }
    },

    async setDefaultPurchaseType() {
      const res = await getPurchaseTypes();
      const list = res || [];

      const candidates = this.isContractorInitiator
        ? ["CONTRACTOR_SUPPLIER", "CONTRACTOR_SUPPLIER_VIA_TD", "CONTRACTOR_TD", "SMARTREMONT"]
        : ["PROJECT_SUPPLIER", "PROJECT_SUPPLIER_VIA_TD", "PROJECT_TD", "SMARTREMONT"];

      const found =
        list.find((pt) => pt.provider_request_purchase_type_code === "DIRECT") ||
        candidates.map((c) => list.find((pt) => pt.provider_request_purchase_type_code === c)).find(Boolean) ||
        (list.length === 1 ? list[0] : null) ||
        list[0] ||
        null;

      if (found) this.purchaseTypeId = found.provider_request_purchase_type_id;
    },

    async onSubmit() {
      if (this.loading) return;
      if (this.selectedCount === 0) {
        this.showErrorSnackbar("Укажите количество хотя бы для одного материала");
        return;
      }

      const params = this.data?.data?.params || {};
      const sbs_project_id = params.sbs_project_id;

      if (!sbs_project_id) {
        this.showErrorSnackbar("Не указан проект УСП (sbs_project_id)");
        return;
      }
      if (!this.formData.provider_request_initiator_type_id) {
        this.showErrorSnackbar("Выберите инициатора заявки");
        return;
      }
      if (!this.formData.date_shipping) {
        this.showErrorSnackbar("Укажите дату отгрузки");
        return;
      }

      if (!this.purchaseTypeId) {
        try {
          await this.setDefaultPurchaseType();
        } catch (e) {}
      }
      if (!this.purchaseTypeId) {
        this.showErrorSnackbar("Не определён тип закупки");
        return;
      }

      this.loading = true;
      let lastResult = null;
      let createdCount = 0;
      let errorCount = 0;

      for (const mat of this.allMaterials) {
        const qty = this.getQty(mat.material_id);
        if (qty <= 0) continue;
        if (this.isRowSuccess(mat.material_id)) continue;

        const agreementId = this.getSelectedAgreementId(mat);
        if (!agreementId) {
          this.setRowResult(mat.material_id, "error", "Не выбран договор поставки");
          errorCount++;
          continue;
        }

        if (this.rowResultMap?.[mat.material_id]?.status === "error") {
          this.clearRowResult(mat.material_id);
        }

        const payload = {
          material_id: mat.material_id,
          qty_sell: qty,
          date_shipping: this.formData.date_shipping,
          provider_request_purchase_type_id: this.purchaseTypeId,
          provider_request_initiator_type_id: this.formData.provider_request_initiator_type_id || null,
          contractor_agreement_list_id: agreementId,
        };
        if (this.formData.sbs_sklad_id) payload.sbs_sklad_id = this.formData.sbs_sklad_id;

        try {
          const res = await createEntranceMaterialRequestOrThrow(payload, { ...params, sbs_project_id });
          lastResult = res;
          createdCount++;
          this.setRowResult(mat.material_id, "success", "Сохранено");
          this.qtyTextMap = { ...this.qtyTextMap, [mat.material_id]: "0" };
        } catch (e) {
          const msg =
            typeof e === "string"
              ? e
              : e?.response?.data?.error || formatErrorMessage(e);
          this.setRowResult(mat.material_id, "error", msg);
          errorCount++;
        }
      }

      this.loading = false;

      if (createdCount > 0 && errorCount === 0) {
        this.showSucceedSnackbar("Материалы добавлены в заявку");
        if (this.data?.onSubmit) await this.data.onSubmit(lastResult);
        return;
      }

      if (createdCount > 0 && errorCount > 0) {
        this.showErrorSnackbar(`Создано позиций: ${createdCount}. Ошибок: ${errorCount}.`);
        if (this.data?.onSubmit) await this.data.onSubmit(lastResult);
      }
    },
  },
  setup() {
    const { closeModal } = useAppStore();
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar();
    return { showSucceedSnackbar, showErrorSnackbar, closeModal };
  },
  mounted() {
    this.loadInitiatorTypes();
    this.loadSkladOptions();
    this.setDefaultPurchaseType();
  },
};
</script>

<style scoped lang="scss">
.form-wrap {
  margin-top: 10px;
}

.form-1c__row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
}

.form-1c__label {
  width: 140px;
  min-width: 140px;
  font-size: 13px;
  color: #333;
  padding-top: 8px;
  padding-right: 10px;
  text-align: right;

  .required {
    color: red;
  }
}

.form-1c__field {
  flex: 1;
  min-width: 0;

  &--readonly {
    padding: 8px 10px;
    background-color: #f8f8f8;
    border: 1px solid #ddd;
    border-radius: 3px;
    font-size: 13px;
    color: #333;
  }

  &--placeholder {
    padding: 8px 10px;
    color: #999;
    font-size: 13px;
  }

  &--warning {
    padding: 8px 10px;
    background-color: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 3px;
    font-size: 12px;
    color: #856404;
  }
}

.form-1c__hint {
  font-size: 11px;
  color: #666;
  margin-top: 4px;
  font-style: italic;
}

.materials-section {
  margin-top: 16px;
  border-top: 1px solid #e0e0e0;
  padding-top: 12px;
}

.materials-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.materials-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.no-materials {
  text-align: center;
  color: #9e9e9e;
  padding: 20px;
  font-style: italic;
  font-size: 13px;
}

.materials-table-wrap {
  overflow-x: hidden;
}

.materials-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  table-layout: fixed;

  th, td {
    padding: 8px 10px;
    border-bottom: 1px solid #e0e0e0;
    text-align: right;
    white-space: normal;
  }

  th {
    background-color: #f5f5f5;
    font-weight: 600;
    color: #555;
    font-size: 12px;
  }

  td.col-name, th.col-name {
    text-align: left;
    white-space: normal;
    min-width: 220px;
  }

  tbody tr:hover {
    background-color: #fafafa;
  }

  tbody tr.row-active {
    background-color: #e8f5e9;

    &:hover {
      background-color: #dcedc8;
    }
  }

  tbody tr.row-no-agreement {
    opacity: 0.6;

    td {
      background-color: #fafafa;
    }
  }

  tfoot td {
    font-weight: 600;
    border-top: 2px solid #bdbdbd;
  }

  td.total-label {
    text-align: right;
    color: #555;
  }

  td.total-sum {
    color: #2e7d32;
    font-size: 14px;
  }
}

.col-name       { width: 40%; }
.col-price-unit { width: 15%; }
.col-limits     { width: 25%; }
.col-order      { width: 20%; }

.materials-search-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.materials-search {
  flex: 1;
  max-width: 320px;
}

.materials-search-count {
  font-size: 12px;
  color: #777;
  white-space: nowrap;
}

.no-search-result {
  text-align: center;
  padding: 20px;
  color: #999;
  font-style: italic;
  font-size: 13px;
}

.mat-name {
  font-size: 13px;
  color: #333;
}

.mat-agreement {
  margin-top: 5px;
}

.agreement-label {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  color: #fff;
  background-color: #1976d2;
  border-radius: 3px;
  padding: 1px 5px;
  margin-right: 4px;
  letter-spacing: 0.3px;
  vertical-align: middle;

  &--none {
    background-color: #bdbdbd;
  }
}

.agreement-select-wrap {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.no-agreement-text {
  font-size: 11px;
  color: #b0b0b0;
  font-style: italic;
}

.single-agreement-text {
  font-size: 11px;
  color: #444;
}

.cell-stack {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cell-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  white-space: nowrap;
}

.cell-label {
  color: #777;
  font-size: 11px;
}

.cell-main {
  font-weight: 600;
}

.cell-sub {
  color: #666;
  font-size: 11px;
}

.cell-nowrap {
  white-space: nowrap;
}

.cell-row--sum {
  justify-content: flex-end;
}

.qty-input {
  width: 120px;
}

.no-price {
  color: #aaa;
}

.qty-zero {
  color: #bbb;
}

.row-sum {
  color: #2e7d32;
  font-weight: 500;
}

.row-message__cell {
  text-align: left !important;
  white-space: normal !important;
  font-size: 12px;
  padding: 6px 10px;
}

.row-message--success .row-message__cell {
  color: #1b5e20;
  background: #f1f8e9;
}

.row-message--error .row-message__cell {
  color: #b71c1c;
  background: #ffebee;
}

.submit-hint {
  margin-top: 12px;
  font-size: 13px;
  color: #1565c0;
  text-align: right;
  padding-right: 4px;
}
</style>
